import { createContext, useContext } from 'react';

export type AgenthubInterrupt = {
  id: string;
  reason: string;
  message?: string;
  toolCallId?: string;
  // Set only for interrupts that arrived live over SSE, where `id` is the
  // run-event id the backend can resolve directly. Omitted on the persisted
  // rehydration path, whose id is not a run-event id — sending it there would
  // resolve nothing and strand the answer.
  eventId?: string;
  // Present when a delegated specialist raised the question. The card owning
  // that tool call renders it; the thread-global slot stays free for the
  // orchestrator's own interrupts.
  parentToolCallId?: string;
  metadata?: { data?: Record<string, unknown> };
};

export type AgenthubResumeEntry = {
  id?: string;
  interruptId?: string;
  event_id?: string;
  status: string;
  payload?: unknown;
};

export type AgenthubInterruptResponse = AgenthubResumeEntry;

export type AgenthubInterruptContextValue = {
  interrupts: AgenthubInterrupt[];
  submitInterruptResponses: (responses: AgenthubInterruptResponse[]) => Promise<void>;
  runExpired: boolean;
  restartConversation: () => Promise<void>;
};

export const AgenthubInterruptContext = createContext<AgenthubInterruptContextValue>({
  interrupts: [],
  submitInterruptResponses: async () => {},
  runExpired: false,
  restartConversation: async () => {},
});

export function useAgenthubInterrupts(): AgenthubInterrupt[] {
  return useContext(AgenthubInterruptContext).interrupts;
}

export function useAgenthubSubmitInterruptResponses() {
  return useContext(AgenthubInterruptContext).submitInterruptResponses;
}

export function useAgenthubRunExpired(): boolean {
  return useContext(AgenthubInterruptContext).runExpired;
}

export function useAgenthubRestartConversation() {
  return useContext(AgenthubInterruptContext).restartConversation;
}
